// The prompt service is not here: the browser calls it only through the
// dashboard server, so its address stays on the server.
const CLIENT_RUNTIME_CONFIG_DEFAULTS = {
  VITE_BETTER_AUTH_URL: "",
} as const;

// Mutable, and widened to `string`: these are runtime values, and the reduce
// below builds the object by assignment.
type ClientRuntimeConfig = {
  -readonly [K in keyof typeof CLIENT_RUNTIME_CONFIG_DEFAULTS]: string;
};
type ClientRuntimeConfigKey = keyof ClientRuntimeConfig;

type ClientRuntimeProcessEnv = Partial<
  Record<ClientRuntimeConfigKey | string, string | undefined>
>;

const runtimeConfigKeys = Object.keys(
  CLIENT_RUNTIME_CONFIG_DEFAULTS,
) as ClientRuntimeConfigKey[];

let clientRuntimeConfig: Partial<ClientRuntimeConfig> = {};

const getProcessEnv = (): ClientRuntimeProcessEnv => {
  if (
    typeof globalThis === "object" &&
    "process" in globalThis &&
    globalThis.process &&
    typeof globalThis.process === "object" &&
    "env" in globalThis.process &&
    globalThis.process.env &&
    typeof globalThis.process.env === "object"
  ) {
    return globalThis.process.env as ClientRuntimeProcessEnv;
  }

  return {};
};

const resolveClientRuntimeEnv = (
  env?: ClientRuntimeProcessEnv,
): ClientRuntimeProcessEnv => env ?? getProcessEnv();

export const getClientRuntimeConfig = (
  env?: ClientRuntimeProcessEnv,
): ClientRuntimeConfig => {
  const resolvedEnv = resolveClientRuntimeEnv(env);

  return runtimeConfigKeys.reduce((config, key) => {
    config[key] = resolvedEnv[key] || CLIENT_RUNTIME_CONFIG_DEFAULTS[key];
    return config;
  }, {} as ClientRuntimeConfig);
};

export const setClientRuntimeConfig = (
  config: Partial<ClientRuntimeConfig>,
): void => {
  clientRuntimeConfig = { ...clientRuntimeConfig, ...config };
};

export const resetClientRuntimeConfig = (): void => {
  clientRuntimeConfig = {};
};

export const loadClientRuntimeConfig = async (
  input = "/client-config.json",
): Promise<void> => {
  const response = await fetch(input, {
    credentials: "same-origin",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to load dashboard runtime config (${response.status})`,
    );
  }

  setClientRuntimeConfig(
    (await response.json()) as Partial<ClientRuntimeConfig>,
  );
};

export const getClientRuntimeValue = (
  key: ClientRuntimeConfigKey,
  env?: ClientRuntimeProcessEnv,
): string => {
  const injectedValue = clientRuntimeConfig[key];

  if (typeof injectedValue === "string") {
    return injectedValue;
  }

  return getClientRuntimeConfig(env)[key];
};
